import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Stone } from '../domain/types';

interface Stone3DViewerProps {
  stone: Stone;
  stage: number;
  size?: number;
  /** 自動回転させるか */
  autoRotate?: boolean;
  /** 自動回転速度(段階が高いほど速く) */
  rotationSpeed?: number;
}

/** Three.jsでGLBモデルを描画するReactラッパー。
 *  GLBが失敗した場合はフォールバックとしてSVG幾何学に切り替える(呼び出し側で判定)。 */
export function Stone3DViewer({
  stone,
  stage,
  size = 200,
  autoRotate = true,
  rotationSpeed,
}: Stone3DViewerProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !stone.modelUrl) return;

    const width = size;
    const height = size;

    // シーンセットアップ
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0.4, 2.4);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    mount.appendChild(renderer.domElement);

    // ライト
    const ambient = new THREE.AmbientLight(0xffffff, 0.55);
    scene.add(ambient);

    const keyLight = new THREE.DirectionalLight(0xfff5d4, 1.2);
    keyLight.position.set(2, 3, 3);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(stone.geometryParams.glowColor, 0.7);
    fillLight.position.set(-2, -1, 2);
    scene.add(fillLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
    backLight.position.set(0, 0, -3);
    scene.add(backLight);

    // GLBロード
    const loader = new GLTFLoader();
    let model: THREE.Object3D | null = null;
    let stopAnimation = false;

    loader.load(
      stone.modelUrl,
      (gltf) => {
        model = gltf.scene;

        // スケール調整
        const targetScale = stone.modelScale ?? 1.0;
        model.scale.setScalar(targetScale);

        // 自動センタリング&ノーマライズ
        const box = new THREE.Box3().setFromObject(model);
        const center = new THREE.Vector3();
        box.getCenter(center);
        const size3 = new THREE.Vector3();
        box.getSize(size3);
        const maxAxis = Math.max(size3.x, size3.y, size3.z);
        if (maxAxis > 0) {
          const normScale = (1.2 / maxAxis) * targetScale;
          model.scale.setScalar(normScale);
          model.position.sub(center.multiplyScalar(normScale / targetScale));
        }

        // 段階によるマテリアル調整
        if (stone.modelAutoBrighten !== false) {
          model.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              const mesh = child as THREE.Mesh;
              if (mesh.material) {
                const mat = mesh.material as THREE.MeshStandardMaterial;
                if (mat.emissive) {
                  mat.emissive = new THREE.Color(stone.geometryParams.glowColor);
                  mat.emissiveIntensity = 0.2 + stage * 0.15;
                }
                if (mat.metalness !== undefined) {
                  mat.metalness = Math.min(0.9, 0.5 + stage * 0.08);
                }
              }
            }
          });
        }

        scene.add(model);
      },
      undefined,
      (err) => {
        console.error('GLB load error:', err);
        setError('モデル読み込みに失敗');
      },
    );

    // アニメーション
    const speed = rotationSpeed ?? (0.005 + stage * 0.002);
    let rafId = 0;
    const animate = () => {
      if (stopAnimation) return;
      rafId = requestAnimationFrame(animate);
      if (autoRotate && model) {
        model.rotation.y += speed;
        model.rotation.x = Math.sin(performance.now() / 1500) * 0.15;
      }
      renderer.render(scene, camera);
    };
    animate();

    // クリーンアップ
    return () => {
      stopAnimation = true;
      cancelAnimationFrame(rafId);
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
      renderer.dispose();
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh) {
          const mesh = obj as THREE.Mesh;
          mesh.geometry?.dispose();
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material?.dispose();
          }
        }
      });
    };
  }, [stone, stage, size, autoRotate, rotationSpeed]);

  if (error || !stone.modelUrl) {
    return null;
  }

  return (
    <div
      ref={mountRef}
      style={{
        width: size,
        height: size,
        position: 'relative',
      }}
    />
  );
}
